import type { UseMutationResult } from '@tanstack/react-query';
import { ZfDialog, ZfDialogContent, ZfDialogHeader, ZfDivider, ZfStack, ZfTypography } from '@zafire/react';
import type { AxiosError } from 'axios';
import type { FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';
import { useSurplusSettings } from '../../api';
import type { PanelSettingsDto } from '../../api/generated/client';
import { CashOccurrenceType } from '../../enums';
import type { ISetting } from '../../interfaces/simulation-settings';
import { SettingBoxCashflowTable } from '../settings-drawer';
import { SurplusModalContent } from './surplus/SurplusModalContent';
import { SurplusOverrideToggle } from './surplus/SurplusOverrideToggle';

interface IProps {
    open: boolean;
    handleClose: () => void;
    cashflowSettings?: PanelSettingsDto;
    updateCashflowItem: UseMutationResult<ISetting, AxiosError, ISetting>;
}

export const RecurringCashflowsModal: FunctionComponent<IProps> = ({ open, handleClose, cashflowSettings, updateCashflowItem }) => {
    const { t } = useTranslation();

    const { data: surplusSettings } = useSurplusSettings();

    return (
        <ZfDialog show={open} size="s" onDialogClose={handleClose}>
            <ZfDialogHeader>{t('recurringCashflows')}</ZfDialogHeader>
            <ZfDialogContent>
                <ZfStack direction="column" spacing="medium">
                    <SurplusOverrideToggle cashflowSettingsEnabled={!!cashflowSettings?.enabled} label={t('useIncomeExpenseSurplus')} />
                    {surplusSettings?.enabled ? (
                        <SurplusModalContent cashflowOverrideSettings={surplusSettings} />
                    ) : (
                        <>
                            <ZfDivider />
                            <ZfTypography variant="body-medium">{t('existingRecurringCashflows')}</ZfTypography>
                            <div style={{ boxSizing: 'border-box' }}>
                                <SettingBoxCashflowTable
                                    enabled={!!cashflowSettings?.enabled}
                                    cashOccurrenceType={CashOccurrenceType.RECURRING}
                                    updateItemSettings={updateCashflowItem}
                                    cashflowItems={cashflowSettings?.itemsSettings ?? []}
                                />
                            </div>
                        </>
                    )}
                    <ZfTypography variant="label-small" color="muted">
                        {t('recurringCashflowsModalFooter')}
                    </ZfTypography>
                </ZfStack>
            </ZfDialogContent>
        </ZfDialog>
    );
};
